import { useTranslation } from "react-i18next";
// src/pages/CustomerEntry.tsx
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";

import { apiPost } from "../api/client";
import { Button } from "../components/Button";
import { companyRoute } from "../utils/routeHelpers";
import Inlay from "../components/Inlay";
import { A } from "../styles/appleClasses";

/** ================= Types ================= */
type Customer = {
  id: number | string;
  name: string;
  email?: string | null;
  mobile?: string | null;
  contact?: string | null;
  address?: string | null;
};

type CustomerFormState = {
  name: string;
  email: string;
  mobile: string;
  contact: string;
  address: string;
};

type CommonProps = {
  variant?: "minimal" | "full";
  companyId?: string | number;
  initial?: Partial<CustomerFormState>;
  onCreated?: (customer: Customer) => void;
  title?: string;
  autoFocusName?: boolean;
};

type PageModeProps = CommonProps & { mode?: "page" };
type InlayModeProps = CommonProps & { mode: "inlay"; onClose: () => void };
type Props = PageModeProps | InlayModeProps;

/** ================= Utils ================= */
function isValidEmail(email: string) {
  return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// textarea: same as A.inputBase but without fixed height + with padding-y
const textareaBase = A.inputBase
  .replace("h-10 ", "")
  .replace("px-4 ", "px-4 py-2 ");

// Prevent iPhone/Safari modal auto-zoom on inlay inputs
const modalInputBase =
  `${A.inputBase} text-[16px] sm:text-[13px] ` +
  `placeholder:text-[16px] sm:placeholder:text-[13px]`;

const modalTextareaBase =
  `${textareaBase} text-[16px] sm:text-[13px] ` +
  `placeholder:text-[16px] sm:placeholder:text-[13px]`;

/** ================= Component ================= */
type DisplayError = { local: "nameRequired" | "invalidEmail" | "createFailed" } | { raw: string } | null;

export default function CustomerEntry(props: Props) {
  const { t } = useTranslation();
  const errorText = (error: DisplayError): string | null => error == null ? null : "raw" in error ? error.raw : ({
    nameRequired: t("directory.customers.nameRequired"),
    invalidEmail: t("directory.customers.invalidEmail"),
    createFailed: t("directory.customers.createFailed"),
  })[error.local];

  const {
    variant = "full",
    companyId,
    initial,
    onCreated,
    title = t("directory.customers.new"),
    autoFocusName = true,
  } = props;

  const navigate = useNavigate();
  const { company_id: routeCompanyId } = useParams<{ company_id?: string }>();

  const resolvedCompanyId = useMemo(
    () => (companyId ?? routeCompanyId ?? "").toString(),
    [companyId, routeCompanyId]
  );

  const isInlay = props.mode === "inlay";

  const [form, setForm] = useState<CustomerFormState>({
    name: initial?.name ?? "",
    email: initial?.email ?? "",
    mobile: initial?.mobile ?? "",
    contact: initial?.contact ?? "",
    address: initial?.address ?? "",
  });

  const [error, setError] = useState<DisplayError>(null);
  const [saving, setSaving] = useState(false);

  const iconBtnCls = [
    A.iconBtn,
    "inline-flex items-center justify-center",
    "hover:bg-gray-100",
    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
    "transition",
  ].join(" ");

  const inputCls = isInlay ? modalInputBase : A.inputBase;
  const textareaCls = isInlay ? modalTextareaBase : textareaBase;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  async function createCustomer(): Promise<Customer> {
    const query = resolvedCompanyId
      ? `?company_id=${encodeURIComponent(resolvedCompanyId)}`
      : "";

    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      mobile: form.mobile.trim() || null,
      contact: form.contact.trim() || null,
      address: form.address.trim() || null,
    };

    return apiPost(`/customers${query}`, payload);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    setError(null);

    if (!form.name.trim()) {
      setError({ local: "nameRequired" });
      return;
    }
    if (!isValidEmail(form.email.trim())) {
      setError({ local: "invalidEmail" });
      return;
    }

    setSaving(true);
    try {
      const customer = (await createCustomer()) as Customer;
      onCreated?.(customer);

      if (isInlay) {
        props.onClose();
      } else {
        navigate(companyRoute(resolvedCompanyId, "/customers"));
      }
    } catch {
      setError({ local: "createFailed" });
    } finally {
      setSaving(false);
    }
  };

  /** Reusable form body; provide a cancel handler for current mode */
  const renderFormBody = (cancelHandler: () => void) => (
    <form
      onSubmit={handleSubmit}
      className={[
        "space-y-3",
        saving ? "opacity-60 pointer-events-none" : "",
      ].join(" ")}
      autoComplete="off"
    >
      {/* Customer Name (always) */}
      <div>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          className={inputCls}
          placeholder={t("directory.customers.name")}
          autoFocus={autoFocusName && !isInlay}
          required
          aria-label={t("directory.customers.name")}
          disabled={saving}
        />
      </div>

      {/* Variant-specific fields */}
      {variant === "minimal" ? (
        <div>
          <input
            type="text"
            name="contact"
            value={form.contact}
            onChange={handleChange}
            className={inputCls}
            placeholder={t("directory.customers.contactOptional")}
            aria-label={t("directory.common.contactPerson")}
            disabled={saving}
          />
        </div>
      ) : (
        <>
          <div>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className={inputCls}
              placeholder={t("directory.customers.emailOptional")}
              autoComplete="email"
              aria-label={t("directory.common.emailAddress")}
              disabled={saving}
            />
          </div>

          <div>
            <input
              type="tel"
              name="mobile"
              value={form.mobile}
              onChange={handleChange}
              className={inputCls}
              placeholder={t("directory.customers.mobileOptional")}
              autoComplete="tel"
              aria-label={t("directory.common.mobileNumber")}
              disabled={saving}
            />
          </div>

          <div>
            <input
              type="text"
              name="contact"
              value={form.contact}
              onChange={handleChange}
              className={inputCls}
              placeholder={t("directory.customers.contactOptional")}
              aria-label={t("directory.common.contactPerson")}
              disabled={saving}
            />
          </div>

          <div>
            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              rows={3}
              className={textareaCls}
              placeholder={t("directory.common.addressOptional")}
              aria-label={t("directory.common.address")}
              disabled={saving}
            />
          </div>
        </>
      )}

      {/* Error slot (no layout shift) */}
      <div className="min-h-[20px] text-center">
        {error ? (
          <span className="text-red-500 text-xs font-medium">{errorText(error)}</span>
        ) : null}
      </div>

      {/* Actions (compact) */}
      <div className="flex justify-end gap-3 pt-2">
        <Button
          type="button"
          variant="secondary"
          onClick={cancelHandler}
          disabled={saving}
        >
          {t("directory.common.cancel")}
        </Button>

        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? t("directory.common.savingDots") : t("directory.common.save")}
        </Button>
      </div>
    </form>
  );

  /** ===== Render per mode ===== */
  if (isInlay) {
    return (
      <Inlay backLabel={t("directory.common.back")} closeLabel={t("directory.common.close")} title={title} onClose={props.onClose} onBack={props.onClose}>
        <div className={`p-4 sm:p-6 ${A.body}`}>
          {renderFormBody(props.onClose)}
        </div>
      </Inlay>
    );
  }

  // Page mode (default) — match list pages shell
  return (
    <div className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 ${A.body}`}>
      {/* Header (← title ×) */}
      <div
        className={[
          "sticky top-0 z-30 bg-gray-50 w-full max-w-md",
          "flex items-center border-b border-gray-100 px-2 pb-2 mb-2",
        ].join(" ")}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtnCls}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`flex-1 text-center tracking-tight ${A.title}`}>{title}</h2>

        <button
          type="button"
          onClick={() => navigate(companyRoute(resolvedCompanyId, "/customers"))}
          className={iconBtnCls}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* Card */}
      <div className="w-full max-w-md px-2">
        <div className={`${A.card} p-4 sm:p-6`}>
          {renderFormBody(() => navigate(-1))}
        </div>
      </div>
    </div>
  );
}